import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  TextField,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Reply } from '@mui/icons-material';
import apiService from '../services/apiService';

const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSupportTickets();

      if (response.success) {
        setTickets(response.data);
      } else {
        setError(response.message || 'Failed to load support tickets');
        loadMockData();
      }
    } catch (err) {
      console.error('Support tickets error:', err);
      setError('Failed to load support tickets');
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = () => {
    setTickets([
      {
        id: 'TKT001',
        userName: 'Priya Sharma',
        userType: 'passenger',
        subject: 'Payment issue',
        message: 'I was charged twice for my last ride. Please help.',
        status: 'open',
        priority: 'high',
        createdAt: '2025-01-08 10:00 AM',
      },
      {
        id: 'TKT002',
        userName: 'Rajesh Kumar',
        userType: 'driver',
        subject: 'Account verification',
        message: 'My documents have been pending for 3 days.',
        status: 'in_progress',
        priority: 'medium',
        createdAt: '2025-01-08 11:00 AM',
      },
      {
        id: 'TKT003',
        userName: 'Amit Kumar',
        userType: 'passenger',
        subject: 'App crash issue',
        message: 'The app crashes when I try to book a ride.',
        status: 'open',
        priority: 'high',
        createdAt: '2025-01-08 12:00 PM',
      },
    ]);
  };

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setReplyText('');
    setOpenDialog(true);
  };

  const handleReply = async () => {
    if (!replyText) {
      alert('Please enter a reply');
      return;
    }

    try {
      const response = await apiService.replySupportTicket(selectedTicket.id, {
        reply: replyText,
      });

      if (response.success) {
        alert('Reply sent successfully');
        setOpenDialog(false);
        fetchTickets();
      } else {
        alert(response.message || 'Failed to send reply');
      }
    } catch (err) {
      alert('Failed to send reply');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      open: { label: 'Open', color: 'error' },
      in_progress: { label: 'In Progress', color: 'warning' },
      resolved: { label: 'Resolved', color: 'success' },
      closed: { label: 'Closed', color: 'default' },
    };

    const config = statusConfig[status] || statusConfig.open;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getPriorityChip = (priority) => {
    const priorityConfig = {
      high: { label: 'High', color: 'error' },
      medium: { label: 'Medium', color: 'warning' },
      low: { label: 'Low', color: 'info' },
    };

    const config = priorityConfig[priority] || priorityConfig.medium;
    return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'Ticket ID',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.row.id}
        </Typography>
      ),
    },
    {
      field: 'userName',
      headerName: 'User',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.userName}</Typography>
          <Chip label={params.row.userType} size="small" sx={{ mt: 0.5 }} />
        </Box>
      ),
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 200,
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row.message}
        </Typography>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 120,
      renderCell: (params) => getPriorityChip(params.row.priority),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => getStatusChip(params.row.status),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleView(params.row)}>
            <Visibility fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Support Tickets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage customer support tickets
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error} - Showing demo data
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={tickets}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* View Ticket Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Support Ticket Details</DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {selectedTicket.subject}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {getStatusChip(selectedTicket.status)}
                    {getPriorityChip(selectedTicket.priority)}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">User</Typography>
                  <Typography variant="body1">{selectedTicket.userName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">User Type</Typography>
                  <Typography variant="body1">{selectedTicket.userType}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Message</Typography>
                  <Typography variant="body1">{selectedTicket.message}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Your Reply"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your response here..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Reply />}
            onClick={handleReply}
          >
            Send Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportPage;
